import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiSearch, FiCheckCircle, FiX } from 'react-icons/fi';
import { Alert, Table, Tag, Select, Button } from 'antd';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import AppRangePicker from '@/components/common/AppRangePicker';
import PermissionGate from '../../components/auth/PermissionGate';
import { PERMISSIONS } from '../../config/permissions';
import investorService from '../../services/investorService';
import ifaService from '../../services/ifaService';
import orderService from '../../services/orderService';
import useRemoteSelectPage from '../../hooks/useRemoteSelectPage';
import { clearLocalOnboardingState } from '../../utils/investorOnboardingPersistence';
import { useDashboardDrilldownFilters } from '../../helpers/dashboardDrilldown.helpers';

const { Option } = Select;

const DEFAULT_SORT = { sort_by: 'created_at', sort_order: 'desc' };
const DEFAULT_PAGE_SIZE = 15;
const SELECT_PAGE_SIZE = 50;

const getPartnerUuid = (partner) => partner?.id ?? partner?.uuid ?? '';

function getSelectOptionData(option) {
  const opt = Array.isArray(option) ? option[0] : option;
  return opt?.data ?? opt ?? {};
}

function parseDateRangeFromParams(searchParams) {
  const from = searchParams.get('date_from');
  const to = searchParams.get('date_to');
  return from && to ? [dayjs(from), dayjs(to)] : null;
}

function buildFiltersFromUrl(searchParams, drilldown) {
  const activeInPeriod = searchParams.get('active_in_period') === '1';
  let filters = {
    search: searchParams.get('q') || '',
    status: searchParams.get('status') || 'all',
    partner_id: searchParams.get('partner') || '',
    manager_id: searchParams.get('rm') || '',
    bank_approval_status: searchParams.get('bank_approval_status') || '',
    start_date: '',
    end_date: '',
    active_in_period: activeInPeriod,
  };

  if (drilldown.dateRange) {
    if (drilldown.activeInPeriod) {
      return {
        ...filters,
        start_date: drilldown.apiParams.start_date,
        end_date: drilldown.apiParams.end_date,
        active_in_period: true,
      };
    }
    return { ...filters, ...drilldown.filterDates };
  }

  const range = parseDateRangeFromParams(searchParams);
  if (range) {
    if (activeInPeriod) {
      filters.start_date = range[0].format('YYYY-MM-DD');
      filters.end_date = range[1].format('YYYY-MM-DD');
    } else {
      filters.start_date = range[0].startOf('day').format();
      filters.end_date = range[1].endOf('day').format();
    }
  }
  return filters;
}

function buildDateRangeFromUrl(searchParams, drilldown) {
  if (drilldown.dateRange) return drilldown.dateRange;
  return parseDateRangeFromParams(searchParams);
}

function columnSortOrder(sortState, field) {
  if (sortState.sort_by !== field) return null;
  return sortState.sort_order === 'asc' ? 'ascend' : 'descend';
}

function getInvestorListColumns({ sortState }) {
  return [
    {
      title: 'Inv. ID',
      dataIndex: 'numericId',
      key: 'numericId',
      render: (id) => (
        <span className="font-mono font-medium px-2.5 py-1 text-purple-600 bg-purple-50 rounded text-sm whitespace-nowrap">
          {id ?? '—'}
        </span>
      ),
    },
    {
      title: 'Folio ID',
      dataIndex: 'folio_id',
      key: 'folio_id',
      render: (text) => (
        text ? <span className="font-mono text-green-600 font-medium px-2 py-0.5 bg-green-50 rounded text-[13px]">{text}</span> : <span className="text-gray-400">—</span>
      ),
    },
    {
      title: 'Investor Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: columnSortOrder(sortState, 'name'),
      render: (text) => <span className="font-medium text-gray-900 truncate max-w-[200px]">{text || 'N/A'}</span>,
    },
    {
      title: 'Partner',
      dataIndex: 'manager_name',
      key: 'manager_name',
      render: (text) => (
        <span className="text-gray-800 font-medium truncate max-w-[150px]">{text || '—'}</span>
      ),
    },
    {
      title: 'RM Name',
      dataIndex: 'rm_name',
      key: 'rm_name',
      render: (text) => (
        <span className="text-gray-800 font-medium truncate max-w-[150px]">{text || '—'}</span>
      ),
    },
    {
      title: 'PAN',
      dataIndex: 'pan',
      key: 'pan',
      render: (text) => (
        text ? <span className="font-mono text-orange-500 font-medium px-2 py-0.5 bg-orange-50 rounded uppercase">{text}</span> : <span className="font-mono text-red-400 font-medium px-2 py-0.5 bg-red-50 rounded">N/A</span>
      ),
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
      render: (text) => <span className="text-gray-600 tracking-wide font-medium">{text || '—'}</span>,
    },
    {
      title: 'Reg On',
      dataIndex: 'joined_at',
      key: 'joined_at',
      sorter: true,
      sortOrder: columnSortOrder(sortState, 'joined_at'),
      render: (date) => (
        <span className="font-medium px-2.5 py-1 text-teal-600 bg-teal-50 rounded text-xs whitespace-nowrap">
          {date ? dayjs(date).format('DD-MMM-YYYY') : '—'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      align: 'center',
      render: (isActive) => (
        isActive ? <FiCheckCircle className="text-green-600 text-lg mx-auto" /> : <div className="w-2.5 h-2.5 rounded-full bg-red-400 mx-auto" />
      ),
    },
  ];
}

export default function InvestorListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const drilldown = useDashboardDrilldownFilters();

  // Table State
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Advanced Filter States — seeded from URL on first render
  const [filters, setFilters] = useState(() => buildFiltersFromUrl(searchParams, drilldown));
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');
  const [dateRange, setDateRange] = useState(() => buildDateRangeFromUrl(searchParams, drilldown));
  const [selectedPartnerRecord, setSelectedPartnerRecord] = useState(null);
  const [rmList, setRmList] = useState([]);
  const initialPartnerFilterRef = useRef(searchParams.get('partner')?.trim() || '');
  const partnerUrlPrefillDoneRef = useRef(false);

  const [sortState, setSortState] = useState(() => ({
    sort_by: searchParams.get('sort_by') || DEFAULT_SORT.sort_by,
    sort_order: searchParams.get('sort_order') || DEFAULT_SORT.sort_order,
  }));
  const [pagination, setPagination] = useState(() => {
    const page = Number(searchParams.get('page'));
    const limit = Number(searchParams.get('limit'));
    return {
      current: page > 0 ? page : 1,
      pageSize: limit > 0 ? limit : DEFAULT_PAGE_SIZE,
      total: 0,
    };
  });

  const fetchPartnersPage = useCallback(async ({ page, limit, search, signal }) => {
    const res = await ifaService.getAdminIfas({
      page,
      limit,
      search,
      status: 'all',
      signal,
    });
    return {
      items: Array.isArray(res?.users) ? res.users : [],
      pagination: res?.pagination,
    };
  }, []);

  const {
    items: partners,
    loading: loadingPartners,
    handleSearch: handlePartnerSearch,
    handlePopupScroll: handlePartnerPopupScroll,
    handleOpenChange: handlePartnerOpenChange,
    dropdownRender: partnerDropdownRender,
    searchValue: partnerSearchValue,
    clearSearch: clearPartnerSearch,
  } = useRemoteSelectPage({
    fetchPage: fetchPartnersPage,
    getKey: getPartnerUuid,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: filters.partner_id || null,
    prefetchOnMount: Boolean(initialPartnerFilterRef.current),
    prefetchSearch: initialPartnerFilterRef.current,
    onError: (e) => console.error('Failed to load partners:', e),
  });

  const partnerOptions = useMemo(() => {
    const base = partners
      .map((ifa) => {
        const partnerUuid = getPartnerUuid(ifa);
        if (!partnerUuid) return null;
        return {
          value: partnerUuid,
          label: ifa.name || ifa.email || String(partnerUuid),
          partner: ifa,
        };
      })
      .filter(Boolean);

    const partnerId = filters.partner_id;
    if (
      partnerId &&
      selectedPartnerRecord &&
      String(getPartnerUuid(selectedPartnerRecord)) === String(partnerId) &&
      !base.some((o) => String(o.value) === String(partnerId))
    ) {
      return [
        {
          value: partnerId,
          label: selectedPartnerRecord.name || selectedPartnerRecord.email || String(partnerId),
          partner: selectedPartnerRecord,
        },
        ...base,
      ];
    }
    return base;
  }, [partners, filters.partner_id, selectedPartnerRecord]);

  useEffect(() => {
    if (!filters.partner_id || !partners.length) return;
    const match = partners.find((p) => String(getPartnerUuid(p)) === String(filters.partner_id));
    if (match) setSelectedPartnerRecord(match);
  }, [filters.partner_id, partners]);

  // After URL prefetch resolves the partner, clear search input so the Select shows name not UUID.
  useEffect(() => {
    if (!initialPartnerFilterRef.current || partnerUrlPrefillDoneRef.current || !partners.length) return;
    const match = partners.find(
      (p) => String(getPartnerUuid(p)) === String(initialPartnerFilterRef.current)
    );
    if (!match) return;
    partnerUrlPrefillDoneRef.current = true;
    setSelectedPartnerRecord(match);
    clearPartnerSearch();
  }, [partners, clearPartnerSearch]);

  const loadRms = async () => {
    try {
      const data = await orderService.listOrderRms();
      setRmList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchInvestors = useCallback(
    async (paramsObj = {}) => {
      setLoading(true);
      try {
        const page = paramsObj.page || pagination.current;
        const limit = paramsObj.pageSize || pagination.pageSize;
        const currentFilters = paramsObj.currentFilters || filters;
        const currentSort = paramsObj.sort || sortState;

        const apiParams = {
          page,
          limit,
          search: currentFilters.search,
          status: currentFilters.status,
          sort_by: currentSort.sort_by,
          sort_order: currentSort.sort_order,
        };
        if (currentFilters.partner_id) apiParams.manager_id = currentFilters.partner_id;
        if (currentFilters.manager_id) apiParams.rm_uuid = currentFilters.manager_id;
        if (currentFilters.start_date) apiParams.start_date = currentFilters.start_date;
        if (currentFilters.end_date) apiParams.end_date = currentFilters.end_date;
        if (currentFilters.active_in_period) apiParams.active_in_period = true;
        if (currentFilters.bank_approval_status) {
          apiParams.bank_approval_status = currentFilters.bank_approval_status;
        }

        const result = await investorService.getAdminInvestors(apiParams);

        if (result && result.users) {
                    setInvestors(result.users);
          setPagination((prev) => ({
            ...prev,
            current: page,
            pageSize: limit,
            total: result.pagination?.total_records || result.users.length,
          }));
        } else {
          setInvestors([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
        }
      } catch (err) {
        console.error('Failed to fetch investors:', err);
        setInvestors([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const fetchInvestorsRef = React.useRef(fetchInvestors);
  useEffect(() => { fetchInvestorsRef.current = fetchInvestors; }, [fetchInvestors]);

  const filtersRef = React.useRef(filters);
  const sortStateRef = React.useRef(sortState);
  const paginationRef = React.useRef(pagination);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { sortStateRef.current = sortState; }, [sortState]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  useEffect(() => {
    loadRms();
  }, []);

  useEffect(() => {
    fetchInvestors({
      page: pagination.current,
      pageSize: pagination.pageSize,
      currentFilters: filters,
      sort: sortState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filter / sort / pagination state → URL (replace, no history spam)
  useEffect(() => {
    const p = {};
    if (filters.search) p.q = filters.search;
    if (filters.status && filters.status !== 'all') p.status = filters.status;
    if (filters.partner_id) p.partner = filters.partner_id;
    if (filters.manager_id) p.rm = filters.manager_id;
    if (filters.bank_approval_status) p.bank_approval_status = filters.bank_approval_status;

    const isDrilldownDateRange =
      drilldown.metric &&
      drilldown.dateRange?.[0] &&
      drilldown.dateRange?.[1] &&
      dateRange?.[0]?.isSame(drilldown.dateRange[0], 'day') &&
      dateRange?.[1]?.isSame(drilldown.dateRange[1], 'day');

    if (isDrilldownDateRange) {
      p.metric = drilldown.metric;
      p.start_date = dateRange[0].format('YYYY-MM-DD');
      p.end_date = dateRange[1].format('YYYY-MM-DD');
    } else if (dateRange?.[0] && dateRange?.[1]) {
      p.date_from = dateRange[0].format('YYYY-MM-DD');
      p.date_to = dateRange[1].format('YYYY-MM-DD');
      if (filters.active_in_period) p.active_in_period = '1';
    }

    if (pagination.current > 1) p.page = String(pagination.current);
    if (pagination.pageSize !== DEFAULT_PAGE_SIZE) p.limit = String(pagination.pageSize);
    if (sortState.sort_by !== DEFAULT_SORT.sort_by) p.sort_by = sortState.sort_by;
    if (sortState.sort_order !== DEFAULT_SORT.sort_order) p.sort_order = sortState.sort_order;

    setSearchParams(p, { replace: true });
  }, [
    filters,
    dateRange,
    pagination.current,
    pagination.pageSize,
    sortState,
    drilldown.metric,
    drilldown.dateRange,
    setSearchParams,
  ]);

  const goAddInvestor = () => {
    // Clear local wizard state only — no remote DELETE.
    // start-mobile will resume any existing IN_PROGRESS session automatically.
    clearLocalOnboardingState();
    navigate('/investors/onboard');
  };

  // Setup debounce for searching
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== filters.search) {
        const newFilters = { ...filters, search: searchInput };
        setFilters(newFilters);
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchInvestors({ page: 1, pageSize: pagination.pageSize, currentFilters: newFilters, sort: sortState });
      }
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, filters.search, pagination.pageSize]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchInvestors({ page: 1, pageSize: pagination.pageSize, currentFilters: newFilters, sort: sortState });
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    const newFilters = {
      ...filters,
      start_date: dates
        ? filters.active_in_period
          ? dates[0].format('YYYY-MM-DD')
          : dates[0].startOf('day').format()
        : '',
      end_date: dates
        ? filters.active_in_period
          ? dates[1].format('YYYY-MM-DD')
          : dates[1].endOf('day').format()
        : '',
    };
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchInvestors({ page: 1, pageSize: pagination.pageSize, currentFilters: newFilters, sort: sortState });
  };

  const handlePartnerFilterChange = (val, option) => {
    const data = getSelectOptionData(option);
    const nextPartner =
      data?.partner ?? partners.find((p) => String(getPartnerUuid(p)) === String(val)) ?? null;
    setSelectedPartnerRecord(val ? nextPartner : null);
    clearPartnerSearch();
    handleFilterChange('partner_id', val || '');
  };

  const handlePartnerFilterClear = () => {
    setSelectedPartnerRecord(null);
    clearPartnerSearch();
    handleFilterChange('partner_id', '');
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setDateRange(null);
    setSelectedPartnerRecord(null);
    clearPartnerSearch();
    const clearedFilters = {
      search: '',
      status: 'all',
      partner_id: '',
      manager_id: '',
      bank_approval_status: '',
      start_date: '',
      end_date: '',
      active_in_period: false,
    };
    setFilters(clearedFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchInvestors({ page: 1, pageSize: pagination.pageSize, currentFilters: clearedFilters, sort: sortState });
  };

  const handleTableChange = useCallback((newPagination, _, sorter) => {
    const currentSort = sortStateRef.current;
    const currentPagination = paginationRef.current;

    const newSort =
      sorter?.field && sorter?.order
        ? { sort_by: sorter.field, sort_order: sorter.order === 'ascend' ? 'asc' : 'desc' }
        : currentSort;

    const pageChanged =
      newPagination.current !== currentPagination.current ||
      newPagination.pageSize !== currentPagination.pageSize;
    const sortChanged =
      newSort.sort_by !== currentSort.sort_by || newSort.sort_order !== currentSort.sort_order;

    if (!pageChanged && !sortChanged) return;

    if (sortChanged) setSortState(newSort);
    fetchInvestorsRef.current({
      page: sortChanged ? 1 : newPagination.current,
      pageSize: newPagination.pageSize,
      currentFilters: filtersRef.current,
      sort: newSort,
    });
  }, []);

  const resolveInvestorRouteId = (record) => {
    const uuid = record?.id ?? record?.uuid;
    if (uuid != null && String(uuid).trim() !== '') {
      return String(uuid).trim();
    }
    return '';
  };

  const columns = useMemo(() => getInvestorListColumns({ sortState }), [sortState]);
  const isDashboardDrilldown = Boolean(drilldown.bannerLabel);

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Investors</h1>
        </div>

        <PermissionGate permission={PERMISSIONS.INVESTORS_CREATE}>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={goAddInvestor}
              className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white text-sm font-semibold rounded hover:bg-green-800 active:scale-[0.99] transition-all shadow-sm"
            >
              <FiPlus size={16} strokeWidth={3} /> Add New Investor
            </button>
          </div>
        </PermissionGate>
      </div>

      <div className={isDashboardDrilldown ? undefined : 'space-y-4'}>
        {isDashboardDrilldown ? (
          <Alert
            type="info"
            message={drilldown.bannerLabel}
            showIcon
            className="mb-0! rounded-t-2xl rounded-bl-[0!important] rounded-br-[0!important]"
            action={
              <button
                type="button"
                onClick={handleClearFilters}
                aria-label="Clear filters"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-blue-700/70 transition-colors hover:bg-blue-100/50 hover:text-blue-800"
              >
                <FiX size={18} />
              </button>
            }
          />
        ) : null}

        <div
          className={`bg-white border border-gray-100 shadow-sm overflow-hidden flex flex-col ${
            isDashboardDrilldown ? 'rounded-b-2xl rounded-t-none border-t-0' : 'rounded-2xl'
          }`}
        >
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-100 flex flex-col xl:flex-row xl:items-center gap-4 bg-gray-50/50">
          
          <div className="flex flex-wrap items-center gap-3 flex-1">
            <AppRangePicker
              value={dateRange}
              onChange={handleDateChange}
              className="w-full sm:w-auto h-[38px] rounded-lg"
            />

            <Select
              showSearch
              autoClearSearchValue={false}
              placeholder="Filter by Partner..."
              value={filters.partner_id || undefined}
              onChange={handlePartnerFilterChange}
              onClear={handlePartnerFilterClear}
              allowClear
              loading={loadingPartners}
              className="w-full sm:w-[220px] h-[38px]"
              optionFilterProp="label"
              filterOption={false}
              searchValue={partnerSearchValue}
              onSearch={handlePartnerSearch}
              onPopupScroll={handlePartnerPopupScroll}
              onOpenChange={handlePartnerOpenChange}
              dropdownRender={partnerDropdownRender}
              options={partnerOptions}
              labelRender={() => {
                const label =
                  selectedPartnerRecord?.name ||
                  selectedPartnerRecord?.email ||
                  partnerOptions.find((o) => String(o.value) === String(filters.partner_id))?.label;
                return label ? <span>{label}</span> : null;
              }}
            />

            <Select
              showSearch
              placeholder="Filter by RM..."
              value={filters.manager_id || undefined}
              onChange={(val) => handleFilterChange('manager_id', val || '')}
              allowClear
              className="w-full sm:w-[220px] h-[38px]"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {rmList.map((rm) => (
                <Option key={rm.id} value={rm.id}>
                  {rm.name}
                </Option>
              ))}
            </Select>

            <div className="relative w-full sm:w-[280px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, mobile, PAN or Folio ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full h-[38px] pl-9 pr-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all bg-white"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 border-r border-gray-200 pr-3">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Bank review:</span>
              <Select
                value={filters.bank_approval_status || 'all'}
                onChange={(val) => handleFilterChange('bank_approval_status', val === 'all' ? '' : val)}
                className="w-[150px] h-[38px]"
              >
                <Option value="all">All</Option>
                <Option value="pending_review">Pending review</Option>
              </Select>
            </div>
            <div className="flex items-center gap-2 border-r border-gray-200 pr-3">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Status:</span>
              <Select
                value={filters.status}
                onChange={(val) => handleFilterChange('status', val)}
                className="w-[100px] h-[38px]"
              >
                <Option value="all">All</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </div>
            <Button onClick={handleClearFilters} className="h-[38px] border-gray-300 text-gray-600 text-sm">
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            dataSource={investors}
            rowKey={(r, index) => `${r.id || r.uuid}-${index}`}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '30', '50'],
              className: 'px-4 mb-4 mt-4',
            }}
            onChange={handleTableChange}
            className={`custom-table min-w-[900px] [&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap${
              isDashboardDrilldown ? ' [&_.ant-table-container]:rounded-t-none [&_.ant-table]:rounded-t-none' : ''
            }`}
            rowClassName="hover:bg-gray-50 cursor-pointer"
            onRow={(record) => ({
              onClick: () => {
                const investorRouteId = resolveInvestorRouteId(record);
                if (!investorRouteId) return;
                const partnerUuid = record?.partner_uuid;
                const query = partnerUuid
                  ? `?partner_uuid=${encodeURIComponent(String(partnerUuid))}`
                  : '';
                navigate(`/investors/${encodeURIComponent(investorRouteId)}/profile${query}`);
              },
            })}
          />
        </div>
      </div>
      </div>
    </DashboardPageWrapper>
  );
}
